import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Divider,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkAsReadIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getAllNotifications,
  deleteAllTeamNotification,
  deleteNotification,
  markAsRead,
} from "../../redux/reducers/notificationReducer";

const NotificationPanel = ({ onClose, isPopover = false }) => {
  const theme = useSelector((state) => state.themeValue.colors);
  const userId = useSelector((state) => state.user.userId);
  const notifications = useSelector((state) => state.notification?.list || []);
  const dispatch = useDispatch();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [activeTab, setActiveTab] = useState(0);
  const [hoveredNotification, setHoveredNotification] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'

  useEffect(() => {
    if (userId) {
      dispatch(getAllNotifications(userId));
    }
  }, [userId, dispatch]);

  const handleDeleteAll = async () => {
    if (!userId) return;
    dispatch(deleteAllTeamNotification(userId));
    setSnackbarMsg("All notifications deleted");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleDeleteNotification = async (id) => {
    if (!id) return;
    dispatch(deleteNotification(id));
    setSnackbarMsg("Notification deleted");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    if (viewMode === 'detail' && selectedNotification?.id === id) {
      setViewMode('list');
      setSelectedNotification(null);
    }
  };

  const handleMarkAsRead = async (id) => {
    if (!id) return;
    dispatch(markAsRead(id));
    setSnackbarMsg("Notification marked as read");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    
    // Update selected notification if it's the one being marked as read
    if (selectedNotification?.id === id) {
      setSelectedNotification(prev => ({
        ...prev,
        status: "Read"
      }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setViewMode('detail');
    
    // Auto mark as read when clicked
    if (notification.status === "Unread") {
      handleMarkAsRead(notification.id);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedNotification(null);
  };

  // Sort notifications: unread first, then by date (newest first)
  const sortedNotifications = [...notifications].sort((a, b) => {
    if (a.status === "Unread" && b.status !== "Unread") return -1;
    if (a.status !== "Unread" && b.status === "Unread") return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Filter notifications based on active tab
  const filteredNotifications = sortedNotifications.filter(notification => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return notification.status === "Unread"; 
    return true;
  });

  const unreadCount = notifications.filter(n => n.status === "Unread").length;

 if (!notifications || notifications.length === 0) {
  return (
    <Box
      sx={{
        height: isPopover ? "100%" : "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.background,
        textAlign: "center",
        px: 2,
        position: "relative", // 👈 important
      }}
    >
      {/* TOP RIGHT CLOSE ICON */}
      {isPopover && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: theme.text,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <Typography variant="h6" sx={{ color: theme.text }}>
        No notifications found.
      </Typography>
    </Box>
  );
}

  return (
    <Box sx={{ 
      minHeight: isPopover ? "100%" : "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: theme.background, // Use theme background instead of red
    }}>
      {/* Header */}
      <Card
        sx={{
          borderRadius: isPopover ? 0 : 2,
          backgroundColor: theme.background,
          boxShadow: 'none',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              {viewMode === 'detail' && (
                <IconButton 
                  size="small" 
                  onClick={handleBackToList}
                  sx={{ color: theme.text }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h5" sx={{ color: theme.text, fontWeight: 600 }}>
                {viewMode === 'detail' ? 'Notification Details' : 'Notifications'}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {viewMode === 'list' && (
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small" 
                  onClick={handleDeleteAll}
                >
                  Delete All
                </Button>
              )}
              {isPopover && (
                <IconButton onClick={onClose} size="small" sx={{ color: theme.text }}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Tabs - Only show in list view */}
          {viewMode === 'list' && (
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: theme.text,
                  opacity: 0.7,
                  minWidth: 'auto',
                  px: 2,
                  fontSize: '0.875rem'
                },
                '& .Mui-selected': {
                  color:  '#1976d2',
                  opacity: 1,
                  fontWeight: 600
                },
                '& .MuiTabs-indicator': {
                  backgroundColor:  '#1976d2'
                }
              }}
            >
              <Tab label={`All (${notifications.length})`} />
              <Tab label={`Unread (${unreadCount})`} />
            </Tabs>
          )}
        </Box>
        <Divider />
      </Card>

      {/* Content Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: "auto",
        backgroundColor: theme.background,
        // Hide scrollbar but keep scrolling functionality
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {viewMode === 'list' ? (
          // List View
          filteredNotifications.map((notification) => (
            <Box
              key={notification.id}
              px={2}
              sx={{
                color: theme.text,
                backgroundColor: notification.status === "Unread" 
                  ? (theme.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)') 
                  : theme.background,
                borderBottom: `1px solid ${theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                }
              }}
              onClick={() => handleNotificationClick(notification)}
              onMouseEnter={() => setHoveredNotification(notification.id)}
              onMouseLeave={() => setHoveredNotification(null)}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ 
                  flex: 1, 
                  mr: 2, 
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  {notification.status === "Unread" && (
                    <Box 
                      sx={{ 
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#1976d2',
                        marginRight: 1
                      }} 
                    />
                  )}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      // fontSize: '1rem',
                      // fontWeight: 600,
                      color: theme.text,
                      display: 'inline'
                    }}
                  >
                    {notification.title}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    mt={1} 
                    sx={{ 
                      opacity: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%'
                    }}
                  >
                    {notification.comments }
                  </Typography>
                  
                  <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 1 }}>
                    {new Date(notification.created_at).toLocaleString()}
                  </Typography>
                </Box>

                {hoveredNotification === notification.id && (
                  <Box display="flex" gap={0.5} sx={{ 
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                  }}>
                    {notification.status === "Unread" && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.2)' }
                        }}
                      >
                        <MarkAsReadIcon fontSize="small" sx={{ color: '#4caf50' }} />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(notification.id);
                      }}
                      sx={{
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.2)' }
                      }}
                    >
                      <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          ))
        ) : (
          // Detail View
          // Detail View
selectedNotification && (
  <Box p={3} sx={{ 
    wordWrap: 'break-word', 
    overflowWrap: 'break-word',
    maxWidth: '100%',
    boxSizing: 'border-box'
  }}>
    <Box sx={{ mb: 3 }}>
      <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
        {selectedNotification.status === "Unread" && (
          <Box 
            sx={{ 
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              mt: 0.5,
              flexShrink: 0
            }} 
          />
        )}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 600,
            color: theme.text,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {selectedNotification.title}
        </Typography>
      </Box>
      
      <Typography 
        variant="body1" 
        sx={{ 
          opacity: 0.9,
          lineHeight: 1.6,
          color: theme.text,
          mb: 2,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {selectedNotification.comments}
      </Typography>
      
      <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
        Received: {new Date(selectedNotification.created_at).toLocaleString()}
      </Typography>
    </Box>

    <Box display="flex" gap={1} flexWrap="wrap">
      {selectedNotification.status === "Unread" && (
        <Button
          variant="contained"
          startIcon={<MarkAsReadIcon />}
          onClick={() => handleMarkAsRead(selectedNotification.id)}
          sx={{
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#45a049' }
          }}
        >
          Mark as Read
        </Button>
      )}
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => handleDeleteNotification(selectedNotification.id)}
      >
        Delete
      </Button>
    </Box>
  </Box>
)
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationPanel;